import classNames from 'classnames'
import { FC, useState } from 'react'
import { DeepDive } from 'src/services/aiListeningApi/brandProfile.types'
import { brandLabelPill } from './CompetitivePosition'
import styles from './deepDive.module.scss'
import { formatPosition } from '../shared/utils'

const VISIBLE_ROWS = 8

// The spec's consensus scale: every model (High), three in four (Medium),
// fewer (Low).
const consensusLabel = (count: number, of: number) => {
  if (!of) return 'LOW'
  const ratio = count / of
  if (ratio >= 1) return 'HIGH'
  if (ratio >= 0.75) return 'MEDIUM'
  return 'LOW'
}

interface OutcomeTableProps {
  leaderKey?: string
  rows: DeepDive['outcomeTable']
  totalModels: number
}

// Every brand named in the run, in leader order.
export const OutcomeTable: FC<OutcomeTableProps> = ({
  leaderKey,
  rows,
  totalModels,
}) => {
  const [showAll, setShowAll] = useState(false)
  const leader = rows.find((row) => row.key === leaderKey) ?? rows[0]
  const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS)
  const isTruncated = !showAll && rows.length > VISIBLE_ROWS

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Competitive AI Outcome</h2>
      {leader && (
        <div className={styles.leaderBanner}>
          AI Consensus Leader:{' '}
          <span className={styles.leaderName}>
            {leader.name} ({consensusLabel(leader.primaries, totalModels)} -{' '}
            {leader.primaries}/{totalModels} Models recommendation)
          </span>
        </div>
      )}
      <div className={classNames(styles.outcomeGrid, styles.outcomeHead)}>
        <span>Brand</span>
        <span>Appeared</span>
        <span>#1 Recs</span>
        <span>Avg Pos</span>
      </div>
      <div
        className={classNames(styles.outcomeRows, {
          [styles.outcomeRowsFaded]: isTruncated,
        })}
      >
        {visible.map((row) => (
          <div
            className={classNames(styles.outcomeGrid, styles.outcomeRow)}
            key={row.key}
          >
            <span className={styles.outcomeBrand}>
              <strong>
                {row.name}
                {row.label === 'you' && ' (You)'}
              </strong>
              {brandLabelPill(row)}
            </span>
            <span>
              {row.appeared}/{totalModels}
            </span>
            <span>
              {row.primaries}/{totalModels} Models
            </span>
            <strong>{formatPosition(row.avgPosition)}</strong>
          </div>
        ))}
      </div>
      {rows.length > VISIBLE_ROWS && (
        <button
          className={styles.seeAll}
          onClick={() => setShowAll((value) => !value)}
          type="button"
        >
          {showAll ? 'Show less' : 'See all'}
          <svg
            className={classNames(styles.chevron, {
              [styles.chevronOpen]: showAll,
            })}
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.6"
            />
          </svg>
        </button>
      )}
    </section>
  )
}
